import {
  SocialPlatform,
  SocialTrackerItem,
  SocialWindow,
} from '@digital-twins/shared-types'
import { intlLocaleForUi } from '@/i18n'
import { ROUTES } from '@/routes/routes'

/** Platform names are product names: not translated. */
export const SOCIAL_PLATFORM_LABELS: Record<SocialPlatform, string> = {
  reddit: 'Reddit',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  instagram: 'Instagram',
}

export function formatSocialDateTime(
  iso: string | null,
  uiLanguage: string,
): string | null {
  if (!iso) {
    return null
  }
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  return new Intl.DateTimeFormat(intlLocaleForUi(uiLanguage), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

export function formatSocialDate(
  iso: string | null,
  uiLanguage: string,
): string | null {
  if (!iso) {
    return null
  }
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  return new Intl.DateTimeFormat(intlLocaleForUi(uiLanguage), {
    dateStyle: 'medium',
  }).format(date)
}

export function formatSocialCount(value: number, uiLanguage: string): string {
  return new Intl.NumberFormat(intlLocaleForUi(uiLanguage), {
    notation: 'compact',
  }).format(value)
}

/** Deep-link windows, in days; the dates are fixed when the thread starts. */
const SOCIAL_ASK_WINDOW_DAYS = { '7d': 7 } as const
export type SocialAskWindow = keyof typeof SOCIAL_ASK_WINDOW_DAYS

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Threads deep link scoped to the tracker's knowledge collection and to social
 * sources. `question` starts the thread with that question already asked;
 * without it the composer just opens scoped to the tracker. `window` further
 * limits the ask to posts published in that recent window.
 */
export function askAboutTrackerHref(
  tracker: Pick<SocialTrackerItem, 'collectionToken' | 'totalPosts'>,
  options: { question?: string; window?: SocialAskWindow } = {},
): string | null {
  if (!tracker.collectionToken || tracker.totalPosts === 0) {
    return null
  }
  const params = new URLSearchParams()
  params.set('collection', tracker.collectionToken)
  params.set('source', 'social')
  if (options.window) {
    params.set('window', options.window)
  }
  if (options.question) {
    params.set('q', options.question)
  }
  return `${ROUTES.threads}?${params.toString()}`
}

/** The `window` deep-link param as dates ending now; null when absent or unknown. */
export function socialWindowFromParam(
  param: string | null,
  now: Date = new Date(),
): SocialWindow | null {
  const days =
    param && param in SOCIAL_ASK_WINDOW_DAYS
      ? SOCIAL_ASK_WINDOW_DAYS[param as SocialAskWindow]
      : null
  if (days == null) {
    return null
  }
  return {
    from: new Date(now.getTime() - days * DAY_MS).toISOString(),
    to: now.toISOString(),
  }
}
